import {observable, action, computed} from 'mobx';
import _ from 'lodash';
import {auth} from 'main/tools';
import employeeTimeOffStore from 'main/stores/time_off/EmployeeTimeOffStore';
import moment from 'moment';

class EmployeeTimeOffState {
  store = employeeTimeOffStore;
  parentState;
  history;
  employeeId;

  @observable upcomingHolidaysModalOpen = false;
  @observable accounts = [];
  @observable upcomingHolidays = [];
  @observable activeRequest = null;
  @observable notesModalOpen = false;
  @observable policy;
  @observable policySummaryModalOpen = false;
  @observable unlimitedPolicySummaryModalOpen = false;
  @observable policySummaryAccount;
  @observable requestsInteractiveAgent;
  @observable transactionsInteractiveAgent;
  @observable editingManualAdjustment = {};
  @observable errors = {};
  @observable transactionsForecastDate = moment();
  @observable selectedLiabilityBalance = {};

  receiveProps({parentState, history}) {
    this.parentState = parentState;
    this.employeeId = parentState.match.params.id;
    this.history = history;
  }

  async load() {
    await this.store.load(this.employeeId);

    this.accounts = this.store.getAccounts();
    this.policy = this.store.getPolicy();
    this.upcomingHolidays = this.store.getUpcomingHolidays();
    this.employee = this.store.getEmployeeDetails();
  }

  @action requestTimeOff() {
    this.history.push(`time-off/request`);
  }

  @action openUpcomingHolidaysModal() {
    this.upcomingHolidaysModalOpen = true;
  }

  @action closeUpcomingHolidaysModal() {
    this.upcomingHolidaysModalOpen = false;
  }

  @action redirectToEdit(request) {
    this.history.push(`/${this.employeeId}/time-off/request/${request.id}/edit`);
  }

  @action async deleteRequest(request) {
    await this.store.destroy(request);
  }

  @action showNotesModal(request) {
    this.activeRequest = request;
    this.notesModalOpen = true;
  }

  @action closeNotesModal() {
    this.notesModalOpen = false;
  }

  @action setRequestsInteractiveAgent(agent) {
    this.requestsInteractiveAgent = agent;
  }

  @action setTransactionsInteractiveAgent(agent) {
    this.transactionsInteractiveAgent = agent;
  }

  @action openPolicySummaryModal(policySummaryAccount) {
    this.policySummaryAccount = policySummaryAccount;

    if (policySummaryAccount.unlimited) {
      this.unlimitedPolicySummaryModalOpen = true;
    } else {
      this.policySummaryModalOpen = true;
    }
  }

  @action closePolicySummaryModals() {
    this.policySummaryModalOpen = false;
    this.unlimitedPolicySummaryModalOpen = false;
  }

  @action updateTransactionsForecastDate(date) {
    this.transactionsForecastDate = date || moment();
  }

  @action async deleteManualAdjustment(transaction) {
    await this.store.destroy(transaction);
    this.transactionsInteractiveAgent.refresh();
  }

  @action async deleteTimeOffRequest(request) {
    await this.store.destroy(request);
    this.requestsInteractiveAgent.refresh();
    this.transactionsInteractiveAgent.refresh();
  }

  @action async saveManualAdjustment() {
    const {model, errors} = await this.store.patch(this.editingManualAdjustment);

    this.errors = errors;

    if (model) {
      this.closeEditManualAdjustmentModal();
      this.transactionsInteractiveAgent.refresh();
    }
  }

  @computed get showRequestTimeOffButton() {
    return auth.employee.id === this.employeeId &&
      !this.parentState.employee.terminated;
  }

  @computed get showRecordTimeOffButton() {
    if (this.showRequestTimeOffButton) return false;
    return auth.hasAccess('::MANAGE_TIME_OFF') ||
      this.parentState.editDataPermission('::TIME_OFF');
  }

  @computed get currentEmployee() {
    return this.parentState.employee;
  }

  @computed get accountFilterOptions() {
    return this.accounts.map(account => ({
      id: account.id,
      display: account.timeOffType.name
    }));
  }

  @computed get timeOffEditable() {
    return !this.parentState.employee.terminated && auth.hasAccess('::MANAGE_TIME_OFF');
  }

  accountUnit(accountId) {
    return _.find(this.accounts, {id: String(accountId)}).timeOffType.unit;
  }

  transactionListLatestForYear(transactions) {
    return _.find(transactions, transaction => !_.isNull(transaction.balance)) || _.find(transactions, transaction => !_.isNull(transaction.logged));
  }
}

export default EmployeeTimeOffState;
